import * as React from "react";
import { BusinessReport, TemplateGroup } from "../businessReports.types";

interface Options {
  includeHidden?: boolean;
}

export const useGroupedTemplates = (
  templates: BusinessReport[],
  favoriteIds: Set<string>,
  { includeHidden = false }: Options = {},
) =>
  React.useMemo(() => {
    const groups: TemplateGroup[] = [];

    const favoriteTemplates = templates.filter(t => t.is_active && favoriteIds.has(t.id));
    if (favoriteTemplates.length > 0) {
      groups.push({ label: "Favourites", categoryId: null, templates: favoriteTemplates });
    }

    const categoryMap = new Map<string, { name: string; templates: BusinessReport[] }>();
    const uncategorised: BusinessReport[] = [];
    const hiddenTemplates: BusinessReport[] = [];

    for (const template of templates) {
      if (!template.is_active) {
        hiddenTemplates.push(template);
        continue;
      }

      if (favoriteIds.has(template.id)) continue;

      if (template.category?.id) {
        const existing = categoryMap.get(template.category.id);
        if (existing) {
          existing.templates.push(template);
        } else {
          categoryMap.set(template.category.id, { name: template.category.name, templates: [template] });
        }
      } else {
        uncategorised.push(template);
      }
    }

    for (const [categoryId, { name, templates: categoryTemplates }] of categoryMap.entries()) {
      groups.push({ label: name, categoryId, templates: categoryTemplates });
    }

    if (uncategorised.length > 0) {
      groups.push({ label: "Other", categoryId: null, templates: uncategorised });
    }

    if (includeHidden && hiddenTemplates.length > 0) {
      groups.push({ label: "Hidden", categoryId: null, templates: hiddenTemplates });
    }

    return groups;
  }, [templates, favoriteIds, includeHidden]);
